<script setup>
import axios from 'axios';
import { openHubRoom } from '../../../helpers/hubRoom';
import { CAPABILITIES, can, canAssignBrokers, hubReader } from '../../../helpers/roles';
import { hubEmptyState, hubName, setupChecklist } from '../../../helpers/hubEmptyStates';
import { usePolicyLinesStore } from '../../../stores/policyLines';
import { useEventStore } from '../../../stores/event';
import XlEmptyState from '../../xl/XlEmptyState.vue';
import { useAuthStore } from '../../../stores/auth';
import CancelSessionModal from '../../agenda/CancelSessionModal.vue';
import DeleteOfficeHoursModal from '../../agenda/DeleteOfficeHoursModal.vue';
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { listBrokers } from '../../../api/policyholders';
import { fetchOverview } from '../../../api/overview';
import { plural } from '../../../helpers/plural';
import { compactDate } from '../../../helpers/usDate';
import { viewerTimeZone } from '../../../helpers/greeting';
import { useBrokerScopeStore } from '../../../stores/brokerScope';
import BrokerScopePicker from '../../policyholders/BrokerScopePicker.vue';
import XlPopover from '../../xl/XlPopover.vue';
import OverviewEventPopover from './OverviewEventPopover.vue';
import OverviewCounts from './OverviewCounts.vue';
import OverviewGreeting from './OverviewGreeting.vue';
import OverviewMakeSomething from './OverviewMakeSomething.vue';
import OverviewSetupChecklist from './OverviewSetupChecklist.vue';
import OverviewStartingSoon from './OverviewStartingSoon.vue';
import OverviewTeam from './OverviewTeam.vue';
import OverviewWatchlist from './OverviewWatchlist.vue';
import OverviewWaiting from './OverviewWaiting.vue';
import TodayCard from './TodayCard.vue';

const route = useRoute();
const router = useRouter();
const brokerScope = useBrokerScopeStore();

const data = ref(null);
const brokers = ref([]);
const loading = ref(true);
const error = ref('');

const popoverRow = ref(null);
const popoverAnchor = ref(null);
const popoverOpen = ref(false);

const viewer = computed(() => data.value?.viewer ?? {});
// While the day is on its way, what the browser already knows is drawn in
// its place — today's date, the viewer's name, whether they choose a broker —
// so the heading and the "Viewing" picker do not drop in under the reader when
// the answer lands ("the Overview jumps as it loads"). The server's own words
// replace them, in the same format (UsDate::compact, long).
const todayLabel = compactDate(new Date(), { long: true, timeZone: viewerTimeZone() });
const schedule = computed(() => data.value?.schedule ?? { today: [], week: { count: 0, rows: [] } });
// Null when SSO could not be asked, which the row reads as "show nothing" —
// the numbers are withheld rather than rendered as noughts.
const counts = computed(() => data.value?.counts ?? null);
// Null when SSO could not be asked about renewals, which the card reads as
// "show nothing" — "nobody needs a call" is not a claim to make on a guess.
const watchlist = computed(() => data.value?.watchlist ?? null);
// Which tiles this viewer is offered. Empty until the payload lands, so the row
// does not flash a set that is about to change.
const makeAllowed = computed(() => data.value?.make?.allowed ?? []);
// Null for a broker, and on a hub with fewer than two brokers — the block is
// withheld by the server rather than hidden here.
const team = computed(() => data.value?.team ?? null);
const messages = computed(() => data.value?.messages ?? null);

// BrokerScopePicker wants {id, name}; listBrokers() returns first_name/last_name
// and no `name`, so handing it the raw array rendered every option blank.
// Users.vue has always mapped it; this page did not.
const brokerChoices = computed(() =>
  brokers.value.map((broker) => ({
    id: broker.id,
    name: [broker.first_name, broker.last_name].filter(Boolean).join(' ') || broker.email || 'Unnamed broker',
  }))
);

const load = async () => {
  loading.value = true;
  error.value = '';

  try {
    data.value = await fetchOverview({ brokerId: brokerScope.brokerId });
  } catch {
    error.value = 'We could not load your day just now.';
  } finally {
    loading.value = false;
  }
};

const onBrokerChange = (value) => {
  brokerScope.set(value, router, route);
};

// The filter rescopes the page in place — no navigation, which is the point of
// the control.
watch(() => brokerScope.brokerId, load);

const openPopover = (row, anchor) => {
  popoverRow.value = row;
  popoverAnchor.value = anchor;
  popoverOpen.value = true;
};

/**
 * Opening a session opens its room over the Overview, in the hub
 * (HubSessionRoom) — never in the policyholder portal; closing it leaves the
 * Overview where it was.
 */
const act = (row) => {
  openHubRoom(row.id);
};

// ---- the preview's actions (Overview: "The session preview has no way to open the room") ----

const auth = useAuthStore();
/**
 * Edit and Delete, on the rules the Sessions list keeps: the owner and hub
 * staff on any session, a broker on the ones they added. Office Hours goes by
 * its own rule (editsRow), and its Delete by its own dialog.
 */
const managesRow = (row) => {
  if (!row || row.kind === 'office_hours') return false;
  if (can(auth.userRole, CAPABILITIES.MANAGE_HUB_CONTENT, auth.capabilities)) return true;

  return can(auth.userRole, CAPABILITIES.CONTRIBUTE_HUB_CONTENT, auth.capabilities)
    && row.created_by != null
    && Number(row.created_by) === Number(auth.userData?.subdomain_user_id);
};

/**
 * Edit, on the preview. Office Hours by the Sessions page's rule: the owner
 * and hub staff edit the desk, a broker does not (the editor and the API
 * refuse them besides).
 */
const editsRow = (row) => {
  if (row?.kind === 'office_hours') return can(auth.userRole, CAPABILITIES.MANAGE_HUB_CONTENT, auth.capabilities);
  return Boolean(managesRow(row) || (row && row.can_edit === true));
};

const closePreview = () => { popoverOpen.value = false; };

const joinFromPreview = (row) => {
  closePreview();
  act(row);
};

const editFromPreview = (row) => {
  closePreview();
  router.push({ name: 'Edit Session', params: { id: row.id } });
};

const recordingFromPreview = (row) => {
  closePreview();
  router.push(`/recordings/${row.id}`);
};

// Delete asks through the one dialog the Sessions page uses
// (CancelSessionModal), which needs the session as the agenda knows it — a
// finished one too, so a date of a series is still asked about its series.
const cancelling = ref(null);

const cancelFromPreview = async (row) => {
  closePreview();
  try {
    const { data: answer } = await axios.get(`${window.store.event_backend_api}/agenda-events/${row.id}`);
    cancelling.value = answer?.data ?? null;
  } catch {
    window.loadNotification?.({ message: "That session couldn't be opened. Try again.", error: true });
  }
};

// Delete on Office Hours: the whole desk, after the dialog that says what goes
// — who is waiting included, so that is asked before it opens rather than
// landing in a card already on screen. Briefly: a slow answer opens it without.
const deletingDesk = ref(false);
const deskWaiting = ref(0);
const deleteDeskFromPreview = async () => {
  closePreview();
  deskWaiting.value = 0;
  const asked = axios.get(`${window.store.event_backend_api}/office-hours/desk`, { params: { tz: viewerTimeZone() } })
    .then(({ data }) => { deskWaiting.value = data?.data?.desk?.today?.waiting ?? 0; })
    .catch(() => {});
  await Promise.race([asked, new Promise((resolve) => { setTimeout(resolve, 400); })]);
  deletingDesk.value = true;
};
const onDeskDeleted = async () => {
  deletingDesk.value = false;
  window.loadNotification?.({ message: 'Office Hours deleted.' });
  await load();
};

const onDeleted = async ({ message } = {}) => {
  cancelling.value = null;
  window.loadNotification?.({ message: message || 'Session deleted.' });
  await load();
};

const envURLs = inject('envURLs', {});
const eventStore = useEventStore();
const policyLines = usePolicyLinesStore();
const linesSettled = ref(false);
const brokersSettled = ref(false);
const brokersRequested = ref(false);

const hiddenKey = () => `xl.setupChecklist.hidden.${window.store?.subdomain ?? ''}`;

const readHidden = () => {
  try {
    return localStorage.getItem(hiddenKey()) === '1';
  } catch {
    return false;
  }
};

const persistHidden = () => {
  try {
    localStorage.setItem(hiddenKey(), '1');
    return true;
  } catch {
    return false;
  }
};

const checklistHidden = ref(readHidden());

const hideChecklist = () => {
  if (checklistHidden.value) return;
  checklistHidden.value = true;
  persistHidden();
};

const NO_FIRST_RUN = { mode: null, hidesBody: false, hidesBook: false, title: '', sentence: '', brokerCard: null };

const reader = computed(() => hubReader(auth));
const hubLabel = computed(() => hubName(eventStore.event.value));
const policyLinesDone = computed(() => (linesSettled.value ? policyLines.options.length > 0 : null));
const brokersDone = computed(() => (brokersSettled.value ? brokers.value.length > 0 : null));

const checklist = computed(() => setupChecklist({
  reader: reader.value,
  role: auth.userRole,
  capabilities: auth.capabilities,
  hubName: hubLabel.value,
  policyLinesDone: policyLinesDone.value,
  policyholders: counts.value?.policyholders ?? null,
  brokersDone: brokersDone.value,
}));

const regionShown = (list) => {
  const [lines, , team] = list.steps;

  return lines.done !== null && team.done !== null && !(lines.done && team.done) && !checklistHidden.value;
};

const ownerFirstRun = () => {
  const list = checklist.value;
  const total = counts.value.policyholders;

  if (total === 0 && !brokerScope.brokerId) {
    return { ...NO_FIRST_RUN, mode: 'page', hidesBody: true, title: list.title, sentence: list.sentence };
  }

  return total > 0 && regionShown(list) ? { ...NO_FIRST_RUN, mode: 'region' } : NO_FIRST_RUN;
};

const brokerFirstRun = () => {
  const brokerCard = hubEmptyState('overview', {
    reader: 'broker',
    role: auth.userRole,
    capabilities: auth.capabilities,
    hubName: hubLabel.value,
    counts: counts.value,
  });

  return brokerCard
    ? { ...NO_FIRST_RUN, hidesBook: true, sentence: brokerCard.sentence, brokerCard }
    : NO_FIRST_RUN;
};

const firstRun = computed(() => {
  if (loading.value || error.value || !counts.value) return NO_FIRST_RUN;

  return reader.value === 'broker' ? brokerFirstRun() : ownerFirstRun();
});

const CHECKLIST_ACTIONS = {
  'policy-lines': () => router.push('/policy-lines'),
  invite: () => router.push({ path: '/policyholders', query: { tab: 'registered', create: 'invite' } }),
  import: () => router.push('/policyholders/import'),
  team: () => window.open(`${envURLs.eco ?? ''}/team`, '_blank', 'noopener'),
  schedule: () => router.push('/sessions/new'),
};

const onChecklistAction = (id) => CHECKLIST_ACTIONS[id]?.();
const onBrokerCardAction = () => router.push({ path: '/sessions', query: { view: 'calendar' } });

const loadBrokers = async () => {
  brokersRequested.value = true;
  try {
    brokers.value = await listBrokers();
  } catch {
    // The page is still usable without the picker's options.
    brokers.value = [];
  } finally {
    brokersSettled.value = true;
  }
};

const loadLines = async () => {
  try {
    await policyLines.load();
  } finally {
    linesSettled.value = true;
  }
};

const startAssignerLoads = () => {
  if (!canAssignBrokers(auth.userData)) return;
  loadBrokers();
  loadLines();
};

onMounted(async () => {
  brokerScope.adoptFromRoute(route);
  startAssignerLoads();
  await load();

  if (viewer.value.can_choose_broker && !brokersRequested.value) await loadBrokers();
});
</script>

<template>
  <div class="page-content" data-xl-gen="northstar" data-testid="overview-page">
    <OverviewGreeting
      :date-label="data?.today?.label ?? todayLabel"
      :first-name="viewer.first_name ?? auth.userData?.first_name ?? ''"
      :sentence="firstRun.sentence || (data?.greeting?.sentence ?? '')"
      :lead="firstRun.sentence ? '' : (data?.greeting?.lead ?? '')"
      :rest="firstRun.sentence ? '' : (data?.greeting?.rest ?? '')"
      :title="firstRun.title"
      :loading="loading"
    />

    <OverviewSetupChecklist
      v-if="firstRun.mode"
      :steps="checklist.steps"
      :mode="firstRun.mode"
      :footer="checklist.footer"
      :footer-action="checklist.footerAction"
      @action="onChecklistAction"
      @hide="hideChecklist"
    />

    <XlEmptyState
      v-if="firstRun.brokerCard"
      variant="page"
      :icon="firstRun.brokerCard.icon"
      :headline="firstRun.brokerCard.headline"
      :body="firstRun.brokerCard.body"
      :actions="firstRun.brokerCard.actions"
      data-xl-gen="northstar"
      data-testid="overview-broker-empty"
      @action="onBrokerCardAction"
    />

    <template v-if="!firstRun.hidesBody">
    <!-- The session you run, about to start or already on: its room is one
         press away, above everything else on the day. -->
    <OverviewStartingSoon v-if="!loading && !error" :rows="schedule.today" @open="act" />

    <!-- Time-sensitive first, as the prototype orders it: what is on today
         comes straight after the greeting, before any view of the book. -->
    <TodayCard
      :rows="schedule.today"
      :loading="loading"
      :error="error"
      :week-count="schedule.week.count"
      :week-has-days-left="!!schedule.week.has_days_left"
      :week-through-label="schedule.week.through_label"
      :week-titles="schedule.week.titles ?? []"
      :week-more="schedule.week.more"
      :week-rows="schedule.week.rows ?? []"
      @open="openPopover"
      @act="act"
      @retry="load"
      @open-sessions="router.push({ path: '/sessions', query: { view: 'calendar' } })"
    />

    <template v-if="!firstRun.hidesBook">
    <OverviewWaiting
      v-if="!loading && !error"
      :messages="messages"
      @open="router.push({ path: '/messages', query: brokerScope.brokerId ? { broker_id: brokerScope.brokerId } : {} })"
    />

    <!--
      Directly above the numbers it scopes, rather than floating at the top of
      the page. A filter detached from what it filters reads as page furniture;
      sitting with the counts, "Viewing" says what it does.
    -->
    <div v-if="(viewer.can_choose_broker ?? canAssignBrokers(auth.userData)) && !error" class="overview__viewing" data-testid="overview-viewing">
      <span class="overview__viewing-label">Viewing</span>
      <BrokerScopePicker
        :can-choose="true"
        :brokers="brokerChoices"
        :model-value="brokerScope.brokerId"
        @update:model-value="onBrokerChange"
      />
    </div>

    <!-- A broker reads their own book; the page says whose, where the owner's
         filter would be (prototype: "Your book · 9 policyholders"). -->
    <p
      v-else-if="viewer.is_broker && !loading && !error && counts"
      class="overview__book"
      data-testid="overview-your-book"
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" aria-hidden="true"><circle cx="9" cy="8" r="3.4" /><path d="M2.8 19.4a6.2 6.2 0 0 1 12.4 0M15.6 4.9a3.4 3.4 0 0 1 0 6.3M17.8 14.1a6.2 6.2 0 0 1 3.4 5.3" /></svg>
      Your book · {{ plural(counts.policyholders ?? 0, 'policyholder') }}
    </p>

    <OverviewCounts
      v-if="!error"
      :counts="counts"
      :loading="loading"
      :heading="viewer.is_broker && !viewer.can_choose_broker ? 'Your book today' : 'The hub today'"
    />

    <!-- Who to call before their renewal, under the numbers it comes from. -->
    <OverviewWatchlist
      v-if="!error"
      :watchlist="watchlist"
      :loading="loading"
    />
    </template>

    <!-- Everything above says what needs you; this says what you can make. -->
    <OverviewMakeSomething v-if="!loading && !error" :allowed="makeAllowed" />

    <!-- Last on the page, as in the prototype: the team is a view of the book
         to drill into, not the first thing a morning needs. Its row click still
         rescopes the whole page. -->
    <OverviewTeam v-if="!loading && !error" :team="team" />
    </template>

    <XlPopover
      v-model:open="popoverOpen"
      :anchor="popoverAnchor"
      labelled-by="overview-popover-title"
    >
      <OverviewEventPopover
        v-if="popoverRow"
        :row="popoverRow"
        :can-manage="managesRow(popoverRow)"
        :can-edit="editsRow(popoverRow)"
        title-id="overview-popover-title"
        @close="closePreview"
        @join="joinFromPreview"
        @edit="editFromPreview"
        @cancel="cancelFromPreview"
        @recording="recordingFromPreview"
        @delete-desk="deleteDeskFromPreview"
      />
    </XlPopover>

    <DeleteOfficeHoursModal v-if="deletingDesk" :waiting="deskWaiting" @close="deletingDesk = false" @deleted="onDeskDeleted" />

    <CancelSessionModal
      v-if="cancelling"
      :session="cancelling"
      @close="cancelling = null"
      @deleted="onDeleted"
    />

  </div>
</template>

<style scoped>
.page-content {
  padding-bottom: var(--setup-page-padding, 40px) !important;

  /*
    The board's two brand tints, redeclared for this page only.

    Generation 1 mixes both a shade pinker than the design — #F5D9FF behind an
    icon square, a tag or a hovered table row, #E2B7F5 in an avatar — and this
    page is measured against the prototype, where they are the pale lilacs
    below (its --tint and --tint-2, and the values Northstar already carries).

    Declared here rather than in the tokens, deliberately and with a cost: the
    same components still draw the pinker tint on Policyholders and
    Communications, so the two pages disagree until the tokens themselves
    move. Scoped on request; the whole-app repaint is the eventual fix.

    On the element rather than in a component: custom properties inherit, so
    one declaration reaches every card, tile, avatar and table below it
    without any of them knowing about it.
  */
  --xl-color-primary-container: #F5EFF9;
  --xl-color-primary-fixed-dim: #EADFF3;
}

/* The broker's own book, named where the owner's filter sits (prototype
   "Your book · 9 policyholders": a small tinted chip). */
.overview__book {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 6px;
  padding: 6px 12px;
  border-radius: 10px;
  background: var(--xl-color-primary-container, #F5EFF9);
  color: var(--xl-color-primary);
  font-size: 12px;
  font-weight: var(--xl-font-weight-semibold, 600);
}

.overview__viewing {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.overview__viewing-label {
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-secondary-text);
}
</style>
